import { getColumnLetter, id } from "../utils";

export * from "./TemplateTab/TemplateWorkbookReader";
export * from "./TemplateTab/TemplateSaveService";

// ── Input types ───────────────────────────────────────────────────────────────

export interface FilterConfigRow {
  key: string;
  displayName: string;
  apiField: string;
  displayField: string | null;
  /** "select" | "multiselect" | "date" | "number" | "text" */
  type: string;
  required: boolean;
  cascadeFrom: string | null;
  dropdownEndpoint: string;
  fixedValue?: string;
  generatedFromColumn?: boolean;
  includeInDataCall?: boolean;
}

export interface ColumnConfigRow {
  key: string;
  displayName: string;
  apiField: string;
  /** "text" | "date" | "number" */
  dataType: string;
  /** Excel number format, e.g. "@", "0", "#,##0.00" */
  excelFormat: string;
}

export type TemplateDataReferenceMode = "standardTable" | "none";

export interface ActiveVisibleSheetState {
  sheetName: string;
  isEmpty: boolean;
}

export interface TemplateWorkbookConfig {
  templateName: string;
  entityType: string;
  entityDataEndpoint: string;
  filters: FilterConfigRow[];
  columns: ColumnConfigRow[];
  hasUdfs: boolean;
  includeUdfs: boolean;
  preselectedUdfs: string;
  dataReferenceMode?: TemplateDataReferenceMode;
}

// ── Helper: filter type → (configuration) type column value ──────────────────

function buildFilterConfigType(filter: FilterConfigRow): string {
  const idField = filter.apiField;
  const nameField = filter.displayField || filter.apiField;
  switch (filter.type) {
    case "select":
      return `select(${idField}, ${nameField})`;
    case "multiselect":
      return `multiselect(${idField}, ${nameField})`;
    case "date":
      return "date";
    case "number":
      return "number";
    case "text":
      return "text";
    default:
      return filter.type;
  }
}

// ── Helper: oData condition for a filter value ───────────────────────────────

function buildODataCondition(filter: FilterConfigRow): string | null {
  if (filter.type === "multiselect") {
    return `${filter.apiField} in (%(${filter.displayName})*)`;
  }

  const needsQuotes =
    filter.type === "select" ||
    filter.type === "text" ||
    filter.type === "date";

  const placeholder = `%(${filter.displayName})`;
  return needsQuotes ? `${filter.apiField} eq '${placeholder}'` : `${filter.apiField} eq ${placeholder}`;
}

// ── Helper: filter → dropdown call string ────────────────────────────────────

function buildDropdownCallString(filter: FilterConfigRow, allFilters: FilterConfigRow[]): string {
  if (!filter.dropdownEndpoint) return "";
  let call = filter.dropdownEndpoint;
  const conditions: string[] = [];

  if (filter.cascadeFrom) {
    const parent = allFilters.find((f) => f.key === filter.cascadeFrom);
    if (parent) {
      const cascadeCondition = buildODataCondition(parent);
      if (cascadeCondition) {
        conditions.push(cascadeCondition);
      }
    }
  }

  if (conditions.length > 0) {
    call += `&FilterValue=${conditions.join(" and ")}`;
  }

  return call;
}

// ── Helper: data call string ─────────────────────────────────────────────────

function buildDataCallString(config: TemplateWorkbookConfig): string {
  let call = config.entityDataEndpoint;

  const includeFilterInDataCall = (filter: FilterConfigRow): boolean => {
    if (typeof filter.includeInDataCall === "boolean") {
      return filter.includeInDataCall;
    }
    if (filter.generatedFromColumn) {
      return true;
    }
    return !filter.cascadeFrom;
  };

  const dataCallFilters = config.filters.filter(includeFilterInDataCall);

  const requiredFilters = dataCallFilters.filter((f) => f.required);
  const optionalFilters = dataCallFilters.filter((f) => !f.required);

  const requiredConditions = requiredFilters
    .map((f) => buildODataCondition(f))
    .filter((condition): condition is string => Boolean(condition));

  const optionalConditions = optionalFilters
    .map((f) => buildODataCondition(f))
    .filter((condition): condition is string => Boolean(condition));

  let filterValue = "";
  if (requiredConditions.length > 0) {
    filterValue = requiredConditions.join(" and ");
    if (optionalConditions.length > 0) {
      filterValue += ` ${optionalConditions.map((condition) => `[and ${condition}]`).join(" ")}`;
    }
  } else if (optionalConditions.length > 0) {
    filterValue = `[${optionalConditions[0]}]`;
    if (optionalConditions.length > 1) {
      filterValue += ` ${optionalConditions
        .slice(1)
        .map((condition) => `[and ${condition}]`)
        .join(" ")}`;
    }
  }

  if (filterValue) {
    call += `&FilterValue=${filterValue}`;
  }

  if (config.includeUdfs) {
    call += "&udf=*";
  }
  return call;
}

// ── Helper: column data type → Excel number format ───────────────────────────

export function getDefaultExcelFormatForDataType(dataType: string): string {
  // Handle old format types (text, date, number)
  switch (dataType?.toLowerCase()) {
    case "date":
      return "m/d/yyyy";
    case "number":
      return "#,##0.00";
    case "text":
      return "@";
    
    // Handle .NET types
    case "string":
      return "@";
    case "boolean":
      return "@";
    case "guid":
      return "@";
    case "int32":
    case "int64":
    case "int16":
      return "0";
    case "single":
    case "double":
    case "decimal":
      return "#,##0.00";
    case "datetime":
    case "datetimeoffset":
    case "date":
      return "m/d/yyyy";
    case "time":
    case "timespan":
      return "h:mm:ss AM/PM";
    case "byte":
    case "sbyte":
      return "0";
    default:
      return "@";
  }
}

export async function getActiveVisibleSheetState(): Promise<ActiveVisibleSheetState | null> {
  try {
    return await Excel.run(async (context) => {
      const activeSheet = context.workbook.worksheets.getActiveWorksheet();
      activeSheet.load("name,visibility");
      const usedRange = activeSheet.getUsedRangeOrNullObject(true);
      usedRange.load("address");
      await context.sync();

      if (activeSheet.visibility !== Excel.SheetVisibility.visible) {
        return null;
      }

      return {
        sheetName: activeSheet.name,
        isEmpty: usedRange.isNullObject,
      };
    });
  } catch (err) {
    console.error("getActiveVisibleSheetState error:", err);
    return null;
  }
}

export async function watchActiveVisibleSheetState(onChange: () => void | Promise<void>): Promise<() => Promise<void>> {
  let worksheetActivatedHandler: any = null;
  let worksheetChangedHandler: any = null;

  await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheetActivatedHandler = worksheets.onActivated.add(async () => {
      await onChange();
    });
    worksheetChangedHandler = worksheets.onChanged.add(async () => {
      await onChange();
    });
    await context.sync();
  });

  return async () => {
    await Excel.run(async (context) => {
      worksheetActivatedHandler?.remove();
      worksheetChangedHandler?.remove();
      await context.sync();
    });
  };
}

async function addDataReferenceToEmptyActiveSheet(
  activeSheet: Excel.Worksheet,
  mode: TemplateDataReferenceMode
): Promise<string | null> {
  if (mode === "none") {
    return null;
  }

  activeSheet.load("name,visibility");
  const usedRange = activeSheet.getUsedRangeOrNullObject(true);
  usedRange.load("address");
  await activeSheet.context.sync();

  if (activeSheet.visibility !== Excel.SheetVisibility.visible || !usedRange.isNullObject) {
    return null;
  }

  if (mode === "standardTable") {
    const anchorCell = activeSheet.getRange("A1");
    anchorCell.formulas = [["=data"]];
    await activeSheet.context.sync();

    const tableRange = anchorCell.getSurroundingRegion();
    tableRange.format.autofitColumns();

    try {
      const table = activeSheet.tables.add(tableRange, true /*hasHeaders*/);
      table.style = "TableStyleMedium2";
      await activeSheet.context.sync();
    } catch {
      // Some Excel hosts do not allow converting the dynamic array output into a table.
      const headerRow = tableRange.getRow(0);
      headerRow.format.fill.color = "#D9EAF7";
      headerRow.format.font.bold = true;
      await activeSheet.context.sync();
    }
  }

  return null;
}

// ── Names of known BMX hidden sheets ─────────────────────────────────────────

const KNOWN_HIDDEN_SHEET_NAMES = [
  "(configuration)",
  "(parameters)",
  "(data)",
  "(columns)",
  "(sheets)",
];

const KNOWN_NAMED_RANGES = [
  "template",
  "data",
  "report",
  "parameters",
  "parameters_table",
  "sample",
];

function isDataWorksheetName(sheetName: string): boolean {
  return sheetName === "(data)" || /^\(data-.+\)$/.test(sheetName);
}

function getDataNameId(sheetName: string): string {
  if (sheetName === "(data)") {
    return "data";
  }

  return id(sheetName.substring(1, sheetName.length - 1));
}

function getSheetFormulaName(sheetName: string): string {
  return sheetName.replace(/'/g, "''");
}

function formulaReferencesDataName(formula: unknown, dataNameIds: string[]): formula is string {
  if (typeof formula !== "string" || !formula.startsWith("=")) {
    return false;
  }

  return dataNameIds.some((dataNameId) => {
    const escapedName = dataNameId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(^|[^A-Za-z0-9_])'?${escapedName}'?([^A-Za-z0-9_]|$)`, "i").test(formula.substring(1));
  });
}

async function refreshVisibleDataReferenceFormulas(
  context: Excel.RequestContext,
  sheets: Excel.WorksheetCollection,
  dataNameIds: string[]
): Promise<void> {
  for (const sheet of sheets.items) {
    sheet.load("name,visibility");
  }
  await context.sync();

  for (const sheet of sheets.items) {
    if (sheet.visibility !== Excel.SheetVisibility.visible || isDataWorksheetName(sheet.name)) {
      continue;
    }

    const usedRange = sheet.getUsedRangeOrNullObject();
    usedRange.load("rowCount,columnCount,formulas");
    await context.sync();

    if (usedRange.isNullObject) {
      continue;
    }

    const formulas = usedRange.formulas as unknown[][];
    for (let rowIndex = 0; rowIndex < usedRange.rowCount; rowIndex++) {
      for (let columnIndex = 0; columnIndex < usedRange.columnCount; columnIndex++) {
        const formula = formulas[rowIndex]?.[columnIndex];
        if (formulaReferencesDataName(formula, dataNameIds)) {
          usedRange.getCell(rowIndex, columnIndex).formulas = [[formula]];
        }
      }
    }

    await context.sync();
  }
}

async function resetDataNamesBeforeRegeneration(context: Excel.RequestContext): Promise<void> {
  const sheets = context.workbook.worksheets;
  sheets.load("items/name");
  await context.sync();

  const dataWorksheetInfos: { sheetName: string; dataNameId: string; columnCount: number }[] = [];

  for (const sheet of sheets.items.filter((item) => isDataWorksheetName(item.name))) {
    const usedRange = sheet.getUsedRangeOrNullObject();
    usedRange.load("columnCount");
    await context.sync();

    if (!usedRange.isNullObject && usedRange.columnCount > 0) {
      dataWorksheetInfos.push({
        sheetName: sheet.name,
        dataNameId: getDataNameId(sheet.name),
        columnCount: usedRange.columnCount,
      });
    }
  }

  if (dataWorksheetInfos.length === 0) {
    return;
  }

  const names = context.workbook.names;
  names.load("items/name");
  await context.sync();

  for (const namedItem of names.items) {
    if (dataWorksheetInfos.some((info) => namedItem.name.startsWith(`${info.dataNameId}_`))) {
      namedItem.delete();
    }
  }
  await context.sync();

  for (const info of dataWorksheetInfos) {
    const dataName = context.workbook.names.getItemOrNullObject(info.dataNameId);
    await context.sync();

    if (!dataName.isNullObject) {
      const escapedSheetName = getSheetFormulaName(info.sheetName);
      dataName.formula = `='${escapedSheetName}'!$A$1:$${getColumnLetter(info.columnCount)}$1`;
    }
  }

  await refreshVisibleDataReferenceFormulas(
    context,
    sheets,
    dataWorksheetInfos.map((info) => info.dataNameId)
  );
  context.workbook.application.calculate(Excel.CalculationType.fullRebuild);
  await context.sync();
}

// ── Delete existing BMX sheets and named ranges ───────────────────────────────

async function deleteTemplateSheets(context: Excel.RequestContext): Promise<void> {
  // ── 1. Delete known hidden sheets ──────────────────────────────────────────
  const sheetObjects: Excel.Worksheet[] = [];
  for (const sheetName of KNOWN_HIDDEN_SHEET_NAMES) {
    const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
    sheetObjects.push(sheet);
  }
  await context.sync();

  for (let i = 0; i < sheetObjects.length; i++) {
    const sheet = sheetObjects[i];
    if (!sheet.isNullObject) {
      try {
        sheet.protection.unprotect();
      } catch {
        // ignore if not protected
      }
      sheet.delete();
    }
  }

  // ── 2. Scan all sheets for (data-*) and (columns-*) patterns ─────────────
  const allSheets = context.workbook.worksheets;
  allSheets.load("items/name");
  await context.sync();

  for (const sheet of allSheets.items) {
    if (/^\(data-.+\)$/.test(sheet.name) || /^\(columns-.+\)$/.test(sheet.name)) {
      try {
        sheet.protection.unprotect();
      } catch {
        // ignore
      }
      sheet.delete();
    }
  }

  // ── 3. Delete known named ranges ──────────────────────────────────────────
  const namedObjects: Excel.NamedItem[] = [];
  for (const rangeName of KNOWN_NAMED_RANGES) {
    const named = context.workbook.names.getItemOrNullObject(rangeName);
    namedObjects.push(named);
  }
  await context.sync();

  for (const named of namedObjects) {
    if (!named.isNullObject) {
      named.delete();
    }
  }

  // ── 4. Scan all named ranges for data_* / data-* patterns ─────────────────
  const allNames = context.workbook.names;
  allNames.load("items/name");
  await context.sync();

  for (const namedItem of allNames.items) {
    const n = namedItem.name;
    if (n.startsWith("data_") || n.startsWith("data-")) {
      namedItem.delete();
    }
  }

  await context.sync();
}

// ── Build (configuration) sheet values 2D array ──────────────────────────────

function buildConfigurationValues(config: TemplateWorkbookConfig): (string | number | boolean)[][] {
  const rows: (string | number | boolean)[][] = [];

  // Row 0 — header / template metadata (always skipped by ReportParams slice(1, -N))
  rows.push([config.templateName, "", config.entityDataEndpoint, ""]);

  // Rows 1..N — filter parameter definitions
  for (const filter of config.filters) {
    rows.push([
      filter.displayName,
      buildFilterConfigType(filter),
      buildDropdownCallString(filter, config.filters),
      "",
    ]);
  }

  // Last row — data definition
  rows.push([
    config.entityType,
    "(data)",
    buildDataCallString(config),
    "",
  ]);

  return rows;
}

// ── Build (columns) sheet values 2D array ────────────────────────────────────

function buildColumnsValues(config: TemplateWorkbookConfig): (string | number | boolean)[][] {
  const rows: (string | number | boolean)[][] = [];

  // Row 0 — header (skipped by ReportParams slice(1))
  rows.push(["Field", "Type", "Name", "Filter", "Call"]);

  // Rows 1..N — column definitions
  for (const col of config.columns) {
    rows.push([col.apiField, col.excelFormat || getDefaultExcelFormatForDataType(col.dataType), col.displayName, "", ""]);
  }

  return rows;
}

// ── Main: generate template workbook structure ────────────────────────────────

export async function generateTemplateWorkbook(config: TemplateWorkbookConfig): Promise<string | null> {
  return await Excel.run(async (context) => {
    const initialActiveSheet = context.workbook.worksheets.getActiveWorksheet();
    initialActiveSheet.load("name");
    await context.sync();
    const startingSheetName = initialActiveSheet.name;

    // 1. Shrink existing data names before rebuilding sheets so visible =data spills refresh cleanly.
    await resetDataNamesBeforeRegeneration(context);

    // 2. Clean up any existing BMX sheets
    await deleteTemplateSheets(context);

    // 3. Create (configuration) sheet
    const configSheet = context.workbook.worksheets.add("(configuration)");
    const configValues = buildConfigurationValues(config);
    const configRange = configSheet.getRange(
      `A1:D${configValues.length}`
    );
    configRange.values = configValues as any[][];
    configSheet.visibility = Excel.SheetVisibility.hidden;
    configSheet.protection.protect();

    // 4. Create (columns) sheet
    const colsSheet = context.workbook.worksheets.add("(columns)");
    const colsValues = buildColumnsValues(config);
    const colsRange = colsSheet.getRange(
      `A1:E${colsValues.length}`
    );
    const colsTypeRange = colsSheet.getRange(`B1:B${colsValues.length}`);
    // Keep formatting tokens (for example 0.00) as literal text.
    colsTypeRange.numberFormat = Array(colsValues.length).fill(["@"]) as any[][];
    colsRange.values = colsValues as any[][];
    colsSheet.visibility = Excel.SheetVisibility.hidden;
    colsSheet.protection.protect();

    // 5. Create (data) sheet with column header row
    const dataSheet = context.workbook.worksheets.add("(data)");
    const colCount = config.columns.length || 1;
    const colLetter = getColumnLetter(colCount);
    const headerValues = [
      config.columns.length > 0
        ? config.columns.map((c) => c.displayName)
        : ["Data"],
    ];
    const headerRange = dataSheet.getRange(`A1:${colLetter}1`);
    headerRange.numberFormat = [Array(colCount).fill("@")] as any[][];
    headerRange.values = headerValues as any[][];
    dataSheet.visibility = Excel.SheetVisibility.hidden;
    dataSheet.protection.protect();

    // 6. Named range: "template" → (configuration)!$A$1  (holds template name)
    const templateCell = configSheet.getRange("$A$1");
    templateCell.load("address");

    // 7. Named range: "data" → (data)!$A$1:${colLetter}$1  (header row only until data is loaded)
    const dataHeaderRange = dataSheet.getRange(`$A$1:$${colLetter}$1`);
    dataHeaderRange.load("address");
    await context.sync();

    context.workbook.names.add("template", `=${templateCell.address}`);
    context.workbook.names.add("data", `=${dataHeaderRange.address}`);

    // 8. Create (parameters) sheet with pre-selected UDFs if applicable
    if (config.includeUdfs && config.preselectedUdfs) {
      const paramsSheet = context.workbook.worksheets.add("(parameters)");
      paramsSheet.getRange("A1:B1").values = [["Analyzer:", config.templateName]];
      paramsSheet.getRange("A2:B2").values = [["Parameters:", "..."]];
      const paramsRange = paramsSheet.getRange("A3:C3");
      paramsRange.numberFormat = [["@", "@", "@"]];
      paramsRange.values = [["UDFs", config.preselectedUdfs, config.preselectedUdfs]];
      paramsSheet.visibility = Excel.SheetVisibility.hidden;
      paramsSheet.protection.protect();
    }

    let warning: string | null = null;
    const startingSheet = context.workbook.worksheets.getItemOrNullObject(startingSheetName);
    startingSheet.load("name");
    await context.sync();

    const targetSheet = !startingSheet.isNullObject
      ? startingSheet
      : context.workbook.worksheets.getActiveWorksheet();

    if ((config.dataReferenceMode ?? "none") !== "none") {
      warning = await addDataReferenceToEmptyActiveSheet(targetSheet, config.dataReferenceMode ?? "none");
    }

    try {
      targetSheet.load("visibility");
      await context.sync();
      if (targetSheet.visibility === Excel.SheetVisibility.visible) {
        targetSheet.activate();
        await context.sync();
      }
    } catch {
      // Activation is not required for successful generation.
    }

    return warning;
  });
}

